import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './UltraMembershipOptions.module.scss';
import * as types from '../../../types';
import { centsToDollars } from '../../../lib/util';

interface UltraMembershipOptionsProps {
  subscriptions: types.ISubscriptionProduct[];
  selectedSubscription: types.ISubscriptionProduct | undefined;
  onSelectSubscription: (subscription: types.ISubscriptionProduct) => void;
}

export default function UltraMembershipOptions({
  subscriptions,
  selectedSubscription,
  onSelectSubscription,
}: UltraMembershipOptionsProps) {
  const { t } = useTranslation();

  const formatPrice = (priceInCents: number) => {
    const dollars = centsToDollars(priceInCents);
    return `$${dollars.replace(/\.00$/, '')}`;
  };

  const formatWeeklyPrice = (priceInCents: number, months: number) => {
    const weeks = (months * 52) / 12;
    const weeklyPrice = priceInCents / weeks / 100;
    return `$${weeklyPrice.toFixed(2)}/${t('week')}`;
  };

  const formatDuration = (months: number) => {
    if (months < 12) {
      return t('ultra_x_months', { months });
    }
    const years = months / 12;
    return years === 1 ? t('ultra_1_year') : t('ultra_x_years', { years });
  };

  return (
    <div className={styles.membershipSection}>
      <div className={styles.membershipHeader}>
        <h2 className={styles.membershipTitle}>{t('ultra_pick_your_membership')}</h2>
        <p className={styles.membershipSubtitle}>{t('ultra_hardware_kit_cost')}</p>
      </div>
      <div className={styles.membershipOptions}>
        {subscriptions.map((subscription) => (
          <div
            key={subscription.sku}
            className={classNames(styles.membershipOption, {
              [styles.selected]: selectedSubscription?.sku === subscription.sku,
            })}
            onClick={() => onSelectSubscription(subscription)}
          >
            <div className={styles.optionDuration}>{formatDuration(subscription.renewalMonths)}</div>
            <div className={styles.optionPrice}>{formatPrice(subscription.pricingInCents.USD)}</div>
            <div className={styles.optionWeekly}>
              {t('ultra_equal_to')} {formatWeeklyPrice(subscription.pricingInCents.USD, subscription.renewalMonths)}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.multiDogDiscount}>
        <span className={styles.multiDogLabel}>{t('ultra_multiple_dogs')}</span>{' '}
        <span className={styles.multiDogText}>{t('ultra_multiple_dogs_discount')}</span>
      </div>

      <div className={styles.existingMemberBox}>
        <div className={styles.existingMemberTitle}>{t('ultra_already_member')}</div>
        <div className={styles.existingMemberDescription}>{t('ultra_bundle_description')}</div>
        <button
          className={styles.openAppButton}
          onClick={() => {
            /* TODO: Implement open app functionality */
          }}
        >
          {t('ultra_open_app')}
        </button>
      </div>
    </div>
  );
}
